import React, { useEffect } from "react";
import { StyleSheet, View } from "react-native";
import { usePostHog } from "posthog-js/react";
import { useAppConfig } from "../AppConfigProvider";
import TurnBasedAssessment from "../containers/assessment/turnbasedAssessment/TurnBasedAssessment";
import RealtimeAssessment from "../containers/assessment/realtimeAssessment/RealtimeAssessment";
import McqAssessment from "../containers/assessment/mcqAssessment/McqAssessment";
import { RouteProp, useRoute } from "@react-navigation/native";
import type { RootStackParamList } from "../navigation/navigation.types";
import Loader from "../components/common/status/Loader/Loader";
import BlankLayout from "../layouts/BlankLayout";
import ErrorLoader from "../components/common/status/ErrorLoader/ErrorLoader";
import { AssessmentType, useGetAssessmentQuery } from "../graphql/generated/graphql";

function CustomCourseAssessmentStudentView() {
  const posthog = usePostHog();
  const appConfig = useAppConfig();
  const route = useRoute<RouteProp<RootStackParamList, "Custom Course Assessment">>();
  const {
    assessment_id: assessmentId,
    course_id: courseId,
    integration_id: integrationId,
    module_id: moduleId,
  } = route.params;
  const {
    data: assessment,
    loading: assessmentLoading,
    error: assessmentError,
  } = useGetAssessmentQuery({
    variables: {
      assessmentId,
    },
    skip: !assessmentId,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const hasTurnBasedAssessments = appConfig?.hasTurnBasedAssessments;
  const assessmentType = assessment?.getAssessment?.data?.assessmentType;

  useEffect(() => {
    posthog?.capture("Assessment Page Visited");
  }, []);

  if (assessmentLoading) {
    return (
      <BlankLayout>
        <View style={styles.loaderContainer}>
          <Loader />
        </View>
      </BlankLayout>
    );
  }

  if (assessmentError || !assessment?.getAssessment?.data) {
    return (
      <BlankLayout>
        <View style={styles.loaderContainer}>
          <ErrorLoader message="Something went wrong! Try reloading the page." />
        </View>
      </BlankLayout>
    );
  }

  if (assessmentType === AssessmentType.Mcq) {
    return <McqAssessment {...{ assessmentId, courseId, integrationId, moduleId }} />;
  }

  if (hasTurnBasedAssessments) {
    return <TurnBasedAssessment {...{ assessmentId, courseId, integrationId, moduleId }} />;
  } else {
    return <RealtimeAssessment {...{ assessmentId, courseId, integrationId, moduleId }} />;
  }
}

const styles = StyleSheet.create({
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
});

export default CustomCourseAssessmentStudentView;
